import { useState, useEffect, useCallback } from 'react';
import { Upload, FileText, Check, Clock, AlertCircle, Trash2, RefreshCw, X } from 'lucide-react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryLabels = {
  psychometric: 'Psychometric Assessment',
  '360_feedback': '360 Feedback',
  performance_review: 'Performance Review',
  upward_survey: 'Upward / Engagement',
  linkedin: 'LinkedIn',
  personal_narrative: 'Personal Narrative'
};

const categoryColors = {
  psychometric: 'bg-state-ground',
  '360_feedback': 'bg-state-observe',
  performance_review: 'bg-state-execute',
  upward_survey: 'bg-state-connect',
  linkedin: 'bg-state-integrate',
  personal_narrative: 'bg-amber-600'
};

const instrumentTypes = {
  psychometric: ['MBTI', 'DISC', 'Hogan HPI', 'Hogan HDS', 'Hogan MVPI', 'Big Five', 'EQi-2.0', 'CliftonStrengths', 'viaEdge', 'SHL OPQ32', 'FIRO-B', '16PF', 'Enneagram', 'Other'],
  '360_feedback': ['Korn Ferry 360', 'DDI 360', 'CCL 360', 'Hogan 360', 'Custom 360', 'Other'],
  performance_review: ['Annual Review', 'Mid-Year Review', 'Development Plan', 'Succession Rating', 'Other'],
  upward_survey: ['Glint', 'Peakon', 'Culture Amp', 'Qualtrics', 'Team Pulse', 'Other'],
  linkedin: ['LinkedIn Profile Export'],
  personal_narrative: ['Reflection']
};

export default function Sources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    category: '',
    instrument_type: '',
    assessment_date: ''
  });

  const fetchSources = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sources`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSources(data);
      }
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.category || !uploadData.instrument_type) {
      toast.error('Please fill all required fields');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('category', uploadData.category);
      formData.append('instrument_type', uploadData.instrument_type);
      if (uploadData.assessment_date) {
        formData.append('assessment_date', uploadData.assessment_date);
      }

      const response = await fetch(`${API_URL}/api/sources/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      toast.success('Document uploaded successfully!');
      setUploadOpen(false);
      setUploadData({ file: null, category: '', instrument_type: '', assessment_date: '' });
      fetchSources();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleProcess = async (sourceId) => {
    setProcessingId(sourceId);
    try {
      const response = await fetch(`${API_URL}/api/sources/${sourceId}/process`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Processing failed');

      toast.success('Document processed successfully!');
      fetchSources();
    } catch (error) {
      toast.error('Failed to process document');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (sourceId) => {
    if (!confirm('Are you sure you want to delete this source?')) return;

    try {
      const response = await fetch(`${API_URL}/api/sources/${sourceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Source deleted');
      fetchSources();
    } catch (error) {
      toast.error('Failed to delete source');
    }
  };

  const getStatusIcon = (status, confidence) => {
    if (status === 'processed') {
      if (confidence === 'high') return <Check className="w-4 h-4 text-green-500" />;
      if (confidence === 'medium') return <Check className="w-4 h-4 text-amber-500" />;
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    if (status === 'processing') return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
    return <Clock className="w-4 h-4 text-slate-500" />;
  };

  const groupedSources = sources.reduce((acc, source) => {
    const cat = source.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(source);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="space-y-8" data-testid="sources-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Sources</h1>
            <p className="text-slate-400">
              {sources.length} document{sources.length !== 1 ? 's' : ''} in your Strata Record
            </p>
          </div>
          
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button className="strata-button-primary" data-testid="upload-source-btn">
                <Upload className="mr-2 h-4 w-4" />
                Upload Source
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate border-slate-mid max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-white">Upload Source Document</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 pt-4">
                <div>
                  <Label className="text-slate-300 mb-2 block">Document Type *</Label>
                  <Select
                    value={uploadData.category}
                    onValueChange={(value) => setUploadData(prev => ({ ...prev, category: value, instrument_type: '' }))}
                  >
                    <SelectTrigger className="strata-input" data-testid="upload-category">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate border-slate-mid">
                      {Object.entries(categoryLabels).filter(([k]) => k !== 'personal_narrative').map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {uploadData.category && (
                  <div className="animate-fade-in">
                    <Label className="text-slate-300 mb-2 block">Instrument / Source *</Label>
                    <Select
                      value={uploadData.instrument_type}
                      onValueChange={(value) => setUploadData(prev => ({ ...prev, instrument_type: value }))}
                    >
                      <SelectTrigger className="strata-input" data-testid="upload-instrument">
                        <SelectValue placeholder="Select instrument" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate border-slate-mid">
                        {instrumentTypes[uploadData.category]?.map(inst => (
                          <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-slate-300 mb-2 block">Assessment Date (Optional)</Label>
                  <Select
                    value={uploadData.assessment_date}
                    onValueChange={(value) => setUploadData(prev => ({ ...prev, assessment_date: value }))}
                  >
                    <SelectTrigger className="strata-input">
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate border-slate-mid">
                      {[2026, 2025, 2024, 2023, 2022, 2021, 2020].flatMap(year => 
                        ['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                          <SelectItem key={`${year}-${q}`} value={`${year}-${q}`}>{year} {q}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300 mb-2 block">File *</Label>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                      uploadData.file ? 'border-amber-600/50 bg-amber-600/5' : 'border-slate-mid hover:border-slate-600'
                    }`}
                    onClick={() => document.getElementById('source-file')?.click()}
                  >
                    <input
                      type="file"
                      id="source-file"
                      className="hidden"
                      accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.txt"
                      onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                      data-testid="upload-file-input"
                    />
                    {uploadData.file ? (
                      <div className="flex items-center justify-center gap-2">
                        <Check className="w-5 h-5 text-amber-500" />
                        <span className="text-white truncate max-w-[200px]">{uploadData.file.name}</span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setUploadData(prev => ({ ...prev, file: null })); }}
                          className="text-slate-400 hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-slate-500 mb-2" />
                        <p className="text-slate-400 text-sm">Click to upload</p>
                        <p className="text-slate-500 text-xs">PDF, DOCX, JPG, PNG</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setUploadOpen(false)}
                    className="flex-1 strata-button-secondary"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpload}
                    disabled={uploading || !uploadData.file || !uploadData.category || !uploadData.instrument_type}
                    className="flex-1 strata-button-primary"
                    data-testid="submit-upload-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Sources by Category */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
          </div>
        ) : sources.length === 0 ? (
          <Card className="strata-card">
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <h3 className="font-display text-xl text-white mb-2">No sources yet</h3>
              <p className="text-slate-400 mb-6">Upload your first leadership document to start building your Strata Record.</p>
              <Button onClick={() => setUploadOpen(true)} className="strata-button-primary">
                <Upload className="mr-2 h-4 w-4" />
                Upload First Source
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedSources).map(([category, categorySources]) => (
              <div key={category}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-3 h-3 rounded-full ${categoryColors[category]}`} />
                  <h2 className="font-display text-lg text-white">{categoryLabels[category]}</h2>
                  <span className="text-slate-500 text-sm">({categorySources.length})</span>
                </div>
                
                <div className="grid gap-4">
                  {categorySources.map((source) => (
                    <Card key={source.source_id} className="strata-card" data-testid={`source-${source.source_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-lg ${categoryColors[source.category]} bg-opacity-20 flex items-center justify-center shrink-0`}>
                              <FileText className="w-5 h-5" style={{ color: categoryColors[source.category].replace('bg-', '') }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{source.filename}</p>
                              <div className="flex items-center gap-3 text-sm">
                                <span className="text-slate-400">{source.instrument_type}</span>
                                {source.assessment_date && (
                                  <span className="text-slate-500 font-mono">{source.assessment_date}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(source.processing_status, source.confidence_score)}
                              <span className={`text-sm ${
                                source.processing_status === 'processed' ? 'text-green-400' :
                                source.processing_status === 'processing' ? 'text-blue-400' :
                                'text-slate-400'
                              }`}>
                                {source.processing_status === 'processed' 
                                  ? `${source.confidence_score} confidence`
                                  : source.processing_status
                                }
                              </span>
                            </div>
                            
                            {source.processing_status === 'uploaded' && (
                              <Button
                                size="sm"
                                onClick={() => handleProcess(source.source_id)}
                                disabled={processingId === source.source_id}
                                className="strata-button-secondary"
                                data-testid={`process-${source.source_id}`}
                              >
                                {processingId === source.source_id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  'Process'
                                )}
                              </Button>
                            )}
                            
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(source.source_id)}
                              className="text-slate-400 hover:text-red-400"
                              data-testid={`delete-${source.source_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Show parsed findings if processed */}
                        {source.processing_status === 'processed' && source.parsed_data?.key_findings?.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-slate-mid/50">
                            <p className="text-sm text-slate-400 mb-2">Key Findings:</p>
                            <ul className="space-y-1">
                              {source.parsed_data.key_findings.slice(0, 3).map((finding, idx) => (
                                <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                                  <span className="text-amber-500 mt-1">•</span>
                                  <span className="truncate">{finding}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
